import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { fetchBoxWithArticles } from "@/lib/home-box.functions";
import type { HomeBoxArticleSummary } from "@/lib/home-box.server";
import { makeSeoDescription } from "@/lib/seo-description";
import { SafeImage } from "@/components/site/SafeImage";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { TopicFaq } from "@/components/site/TopicFaq";
import { buildTopicFaq, topicFaqJsonLd } from "@/lib/topic-faq";

export const Route = createFileRoute("/topics/$slug/")({
  loader: async ({ params }) => {
    const data = await fetchBoxWithArticles({ data: { slug: params.slug } });
    if (!data.box) throw notFound();
    return data;
  },
  head: ({ loaderData }) => {
    const title = `${loaderData?.box?.title ?? "Topic"} | LifeWestWay`;
    const description = makeSeoDescription(
      loaderData?.box?.blurb || "Expert 2026 guides from the LifeWestWay editorial team.",
    );
    const firstImage = (loaderData?.articles as { hero_image_url?: string }[] | undefined)?.[0]
      ?.hero_image_url;
    const faqJson = loaderData?.box
      ? topicFaqJsonLd(
          buildTopicFaq(loaderData.box as any, (loaderData.articles ?? []) as any),
        )
      : null;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [
        ...(loaderData?.box?.slug
          ? [{ rel: "canonical", href: `https://lifewestway.com/topics/${(loaderData as any).box.slug}` }]
          : []),
        ...(firstImage
          ? [{ rel: "preload", as: "image", href: firstImage, fetchPriority: "high" as const }]
          : []),
      ],
      ...(faqJson
        ? { scripts: [{ type: "application/ld+json", children: faqJson }] }
        : {}),
    };
  },
  errorComponent: () => (
    <p className="mx-auto max-w-3xl px-4 py-20 text-muted-foreground">
      This topic could not be loaded. Please try again.
    </p>
  ),
  notFoundComponent: () => (
    <p className="mx-auto max-w-3xl px-4 py-20 text-muted-foreground">Topic not found.</p>
  ),
  component: TopicBoxPage,
});

function TopicBoxPage() {
  const { box, articles, otherBoxes } = Route.useLoaderData() as {
    box: { title: string; slug: string; blurb: string } | null;
    articles: HomeBoxArticleSummary[];
    otherBoxes: { title: string; slug: string }[];
  };
  if (!box) return null;
  const faqItems = buildTopicFaq(box, articles);


  return (
    <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:px-8">
      <Breadcrumbs items={[{ label: "Home", href: "/" }, { label: box.title }]} />
      <h1 className="mt-4 font-display text-3xl font-bold text-foreground sm:text-4xl">
        {box.title}
      </h1>
      <p className="mt-3 max-w-2xl text-muted-foreground">{box.blurb}</p>

      {articles.length === 0 ? (
        <p className="mt-10 rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
          New guides for this topic are being published shortly.
        </p>
      ) : (
        <div className="mt-10 grid gap-6 sm:grid-cols-2">
          {(articles as HomeBoxArticleSummary[]).map((a, i) => (
            <article
              key={a.id}
              className="overflow-hidden rounded-2xl border border-border bg-card transition-colors hover:border-accent/70"
            >
              {a.hero_image_url ? (
                <SafeImage
                  src={a.hero_image_url}
                  alt={a.hero_image_alt || a.title}
                  width={1200}
                  height={675}
                  {...(i === 0 ? { eager: true } : { loading: "lazy" as const })}
                  decoding="async"
                  className="h-44 w-full object-cover"
                />
              ) : null}
              <div className="p-5">
                <h2 className="font-display text-lg font-bold leading-snug text-foreground">
                  <Link
                    to="/topics/$slug/$article"
                    params={{ slug: box.slug, article: a.slug }}
                    title={a.title}
                    className="hover:text-accent"
                  >
                    {a.title}
                  </Link>
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{a.excerpt}</p>
              </div>
            </article>
          ))}
        </div>
      )}

      <TopicFaq items={faqItems} topicTitle={box.title} />

      {otherBoxes.length > 0 ? (
        <nav aria-label="Other topics" className="mt-16 border-t border-border pt-8">
          <h2 className="font-display text-xl font-bold text-accent">Explore other topics</h2>
          <ul className="mt-4 flex flex-wrap gap-2">
            {otherBoxes.map((b) => (
              <li key={b.slug}>
                <Link
                  to="/topics/$slug"
                  params={{ slug: b.slug }}
                  title={b.title}
                  className="inline-block rounded-full border border-accent/30 px-3 py-1.5 text-xs font-semibold text-accent hover:border-accent"
                >
                  {b.title}
                </Link>
              </li>
            ))}
            <li>
              <Link
                to="/topics"
                title="All LifeWestWay topics"
                className="inline-block rounded-full border border-accent/30 px-3 py-1.5 text-xs font-semibold text-accent hover:border-accent"
              >
                All topics
              </Link>
            </li>
          </ul>
        </nav>
      ) : null}
    </div>

  );
}
